import type { StageId, Vec3 } from '../data/content'

// Information flows upward: tokens on the floor, vectors above them, the layer
// stack above that, and the prediction at the top.

export const TILE_H = 0.5
export const TILE_D = 0.55
export const TIGHT_GAP = 0.035
export const SPREAD_GAP = 0.42
export const BLANK_W = 0.95

export const DIMS = 8
export const CUBE = 0.2
export const CUBE_STEP = 0.27
export const COL_BASE = 0.8
export const COL_TOP = COL_BASE + DIMS * CUBE_STEP
export const ARC_Y = COL_TOP + 0.14

export const LAYERS = 6
export const STACK_BASE = 4.0
export const LAYER_GAP = 0.9
export const STACK_TOP = STACK_BASE + (LAYERS - 1) * LAYER_GAP + 0.45

export const BARS_BASE = 9.9
export const BAR_MAX = 3.4
export const BAR_STEP = 1.35

export const CLOUD_CENTER: Vec3 = [0, 4.4, 0]

export function tileWidth(text: string) {
  return clamp(0.42 + text.length * 0.16, 0.72, 2.2)
}

export function rowLayout(widths: number[], gap: number) {
  const total =
    widths.reduce((a, b) => a + b, 0) + gap * Math.max(0, widths.length - 1)
  let x = -total / 2
  const xs = widths.map((w) => {
    const center = x + w / 2
    x += w + gap
    return center
  })
  return { xs, total }
}

export function cubeY(k: number) {
  return COL_BASE + k * CUBE_STEP + CUBE / 2
}

export function barX(k: number, count: number) {
  return (k - (count - 1) / 2) * BAR_STEP
}

export function barHeight(prob: number) {
  return 0.12 + prob * BAR_MAX
}

export function cloudToWorld(p: Vec3): Vec3 {
  return [p[0] + CLOUD_CENTER[0], p[1] + CLOUD_CENTER[1], p[2] + CLOUD_CENTER[2]]
}

export function bezier(a: Vec3, m: Vec3, b: Vec3, t: number): Vec3 {
  const u = 1 - t
  return [
    u * u * a[0] + 2 * u * t * m[0] + t * t * b[0],
    u * u * a[1] + 2 * u * t * m[1] + t * t * b[1],
    u * u * a[2] + 2 * u * t * m[2] + t * t * b[2],
  ]
}

/** Camera framing per stage; `rowWidth` is the spread-out token row. */
export function cameraFor(
  stage: StageId,
  rowWidth: number,
  framing: 'wide' | null = null,
): { pos: Vec3; target: Vec3 } {
  const w = rowWidth
  if (framing === 'wide') {
    return { pos: [0, 7.5, 16 + w * 0.55], target: [0, 6.2, 0] }
  }
  switch (stage) {
    case 'text':
      return { pos: [0, 1.5, 2.6 + w * 0.85], target: [0, 0.35, 0] }
    case 'tokens':
      return { pos: [0, 2.1, 3.2 + w], target: [0, 0.3, 0] }
    case 'embeddings':
      return { pos: [1.4 + w * 0.12, 3.8, 4 + w * 0.95], target: [0, 1.6, 0] }
    case 'meaning':
      return { pos: [9.5, 8.8, 15.5], target: [0.4, CLOUD_CENTER[1], 0] }
    case 'attention':
      return { pos: [0, 5.6, 4.2 + w], target: [0, 2.8, 0] }
    case 'layers':
      return { pos: [-(9.5 + w * 0.5), 8.2, 12 + w * 0.5], target: [0, 4.9, 0] }
    case 'prediction':
      return { pos: [0, 12.8, 11 + w * 0.1], target: [0, 11.1, 0] }
  }
}

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v))
}
